import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import {
  AlertTriangle, CheckCircle2, RotateCcw, Pencil, Sparkles, Loader,
  ChevronDown, ChevronRight,
} from 'lucide-react';

// ============================================================
// NEEDS REVIEW PANEL
//
// Renders at the top of /follow-ups. Lists today's follow-ups that the
// daily AI review flagged as not fitting their rule-based default.
//
// Per row: lead + rule default (muted) + AI flag (highlighted) +
// three action buttons (Accept AI / Keep Rule / Customize). Decision
// is recorded against the follow_up_ai_reviews row via
// POST /follow-ups/:id/review-decision.
//
// BDE sees only their own flagged follow-ups. Admin/TL see all.
// Empty state: shows a small "nothing to review" message that's easy to
// dismiss visually, since most days the panel should be empty (rule
// fits ~90% of the time per the prompt's "bias toward not flagging").
// ============================================================

const DISCREPANCY_META = {
  timing_wrong: {
    label: 'Timing wrong',
    color: '#92400E', bg: '#FFFBEB', border: '#FDE68A',
    summary: 'Wrong date — could be too soon or too late given the conversation.',
  },
  direction_wrong: {
    label: 'Wrong type of message',
    color: '#991B1B', bg: '#FEF2F2', border: '#FECACA',
    summary: 'The kind of follow-up does not match the lead\'s actual stage.',
  },
  lead_state_changed: {
    label: 'Lead is no longer active',
    color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB',
    summary: 'Lead has moved to a closed or dead state but the follow-up is still pending.',
  },
  message_inappropriate: {
    label: 'Tone is off',
    color: '#92400E', bg: '#FFFBEB', border: '#FDE68A',
    summary: 'Type and date are fine but the conversation makes this message feel wrong.',
  },
};

const DECISION_BUTTONS = [
  { key: 'accepted_ai', label: 'Accept AI', icon: CheckCircle2, color: '#10B981' },
  { key: 'kept_rule',   label: 'Keep Rule', icon: RotateCcw,    color: '#6B7280' },
  { key: 'customized',  label: 'Customize', icon: Pencil,       color: '#3B82F6' },
];

const Row = ({ row, onDecision, onCustomize }) => {
  const [open, setOpen] = useState(true);
  const [pendingDecision, setPendingDecision] = useState(null);
  const meta = DISCREPANCY_META[row.discrepancy_type] || DISCREPANCY_META.message_inappropriate;
  const isDecided = row.bde_decision && row.bde_decision !== 'pending';

  const handleClick = async (key) => {
    if (pendingDecision) return;
    setPendingDecision(key);
    try {
      await api.post(`/follow-ups/${row.follow_up_id}/review-decision`, { decision: key });
      onDecision(row.follow_up_id, key);
      if (key === 'customized') {
        // Take the BDE to the same follow-up's row in the regular list
        // below so they can use Generate / Snooze / Mark Sent / Stop.
        onCustomize(row);
      } else {
        toast.success(key === 'accepted_ai' ? 'AI suggestion accepted.' : 'Kept the rule default.');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save decision.');
      setPendingDecision(null);
    }
  };

  return (
    <div style={{
      background: 'white',
      border: '1px solid #E5E7EB',
      borderLeft: `4px solid ${meta.color}`,
      borderRadius: 6,
      marginBottom: 12,
      overflow: 'hidden',
      opacity: isDecided ? 0.65 : 1,
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '12px 14px', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        {open ? <ChevronDown size={14} color="#6B7280" /> : <ChevronRight size={14} color="#6B7280" />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              {row.prospect_name}
            </span>
            <span style={{ fontSize: 13, color: '#6B7280' }}>
              @ {row.company || '(unknown)'}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 600,
              padding: '2px 8px', borderRadius: 999,
              background: meta.color, color: '#FFFFFF',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>{meta.label}</span>
            {isDecided && (
              <span style={{
                fontSize: 10, fontWeight: 600, color: '#065F46',
                padding: '2px 8px', borderRadius: 999, background: '#D1FAE5',
              }}>
                {row.bde_decision === 'accepted_ai' ? 'AI accepted'
                  : row.bde_decision === 'kept_rule' ? 'Kept rule'
                  : 'Customized'}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
            {row.bde_name} · {row.follow_up_type} · {row.follow_up_label}
          </div>
        </div>
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px 36px' }}>
          <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>
            <strong style={{ color: '#374151' }}>Rule was going to:</strong>{' '}
            send a {row.follow_up_type.replace('_', ' ')} message ({row.follow_up_label}) over LinkedIn today.
          </div>
          <div style={{
            padding: 10,
            background: meta.bg,
            border: `1px solid ${meta.border}`,
            borderRadius: 6,
            marginBottom: 10,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: meta.color,
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <AlertTriangle size={12} /> {meta.label}
            </div>
            <div style={{ fontSize: 14, color: '#111827', lineHeight: 1.5 }}>
              {row.explanation || meta.summary}
            </div>
            {row.ai_suggested_action && (
              <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>
                <strong>AI suggests:</strong> {row.ai_suggested_action}
              </div>
            )}
            {row.evidence && (
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6, fontStyle: 'italic' }}>
                Evidence: {row.evidence}
              </div>
            )}
          </div>

          {!isDecided && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {DECISION_BUTTONS.map((btn) => {
                const Icon = btn.icon;
                const isLoading = pendingDecision === btn.key;
                return (
                  <button
                    key={btn.key}
                    onClick={() => handleClick(btn.key)}
                    disabled={!!pendingDecision}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px',
                      background: btn.key === 'accepted_ai' ? btn.color : '#FFFFFF',
                      color: btn.key === 'accepted_ai' ? '#FFFFFF' : btn.color,
                      border: `1px solid ${btn.color}`,
                      borderRadius: 6,
                      fontSize: 13, fontWeight: 500,
                      cursor: pendingDecision ? 'default' : 'pointer',
                      opacity: pendingDecision && !isLoading ? 0.5 : 1,
                    }}
                  >
                    {isLoading ? <Loader size={14} /> : <Icon size={14} />}
                    {btn.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NeedsReviewPanel = () => {
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [date, setDate] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.get('/follow-ups/needs-review')
      .then((res) => {
        setFlagged(res.data.flagged || []);
        setDate(res.data.date || null);
      })
      .catch((e) => {
        setError(e.response?.data?.error || 'Could not load AI review.');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDecision = (followUpId, decision) => {
    setFlagged((prev) => prev.map((r) =>
      r.follow_up_id === followUpId
        ? { ...r, bde_decision: decision, bde_decision_at: new Date().toISOString() }
        : r
    ));
  };

  const handleCustomize = (row) => {
    // Decision is already recorded by the row's handleClick. Now find
    // the same follow-up in the regular Follow-ups list below the panel
    // and scroll + briefly highlight it so the BDE knows where the
    // Generate / Snooze / Mark Sent / Stop actions live for this row.
    //
    // requestAnimationFrame waits one paint after the local state
    // update (the row dims, the decision badge appears) so the element
    // is settled before we scroll.
    requestAnimationFrame(() => {
      const target = document.getElementById(`fu-row-${row.follow_up_id}`);
      if (!target) {
        // Row not currently in the filtered list (e.g. user has a filter
        // pill set that excludes it). Tell them so they can clear it.
        toast('Decision saved. Clear the filter pills to see this follow-up below.', { icon: 'ℹ️' });
        return;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('fu-row-highlight');
      setTimeout(() => target.classList.remove('fu-row-highlight'), 2200);
      toast.success('Decision saved. Use the actions on the highlighted row below.');
    });
  };

  // Don't render anything while loading (avoids flicker above the main
  // follow-ups list). Don't render on hard error either — the panel is
  // additive and shouldn't block the existing page.
  if (loading) return null;
  if (error) return null;

  const pendingCount = flagged.filter((r) => r.bde_decision === 'pending').length;

  return (
    <div className="card" style={{
      marginBottom: 20,
      borderLeft: '4px solid #3B82F6',
    }}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: 0, textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        {collapsed ? <ChevronRight size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
        <Sparkles size={18} color="#3B82F6" />
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            Today's Follow-ups — Needs Review
            {pendingCount > 0 && (
              <span style={{
                marginLeft: 10, fontSize: 12, fontWeight: 600,
                padding: '2px 10px', borderRadius: 999,
                background: '#FEE2E2', color: '#991B1B',
              }}>{pendingCount} pending</span>
            )}
          </h3>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            {flagged.length === 0
              ? `AI checked today's follow-ups${date ? ` for ${date}` : ''}. The rule fits — no review needed.`
              : `AI checked today's follow-ups and flagged ${flagged.length} that may need a different approach.`}
          </div>
        </div>
      </button>

      {!collapsed && flagged.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {flagged.map((row) => (
            <Row
              key={row.follow_up_id}
              row={row}
              onDecision={handleDecision}
              onCustomize={handleCustomize}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NeedsReviewPanel;
